import { Dialog, DialogActions, DialogContent, Button, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FeedFilter } from 'api/KnowledgeAPI';
import {
  getCustomFieldsDefaultValues,
  getCustomFieldsValidationSchema,
  useConfigQuery,
} from '@buildwise/libs/configurations';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FieldWrapper } from '@buildwise/libs/people';
import RenderFilterFieldByType from '@buildwise/libs/feed/components/RenderFilterFieldByType';
import { useTrackerContext } from '../../context';
import { useTrackerDetailsQuery, useTrackerTasksQuery } from '../../queries';
import useUserData from 'hooks/useUserData';
import useTrackerStore from '@buildwise/libs/trackers/store/useTrackerStore';
import TaskStatusMultiSelectField from './TaskStatusMultiSelectField';
import TaskApplicableToMultiSelectField from './TaskApplicableToMultiSelectField';
import TaskTypeMultiSelectField from './TaskTypeMultiSelectField';
import TaskImpactMultiSelectField from './TaskImpactMultiSelectField';
import { ConfigEntryType } from 'config/config';

type TrackerFiltersModalProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

function TrackerFiltersModal({ open, setOpen }: TrackerFiltersModalProps) {
  const { companyId } = useUserData();
  const {
    state: { trackerId },
  } = useTrackerContext();
  const { tracker } = useTrackerDetailsQuery({
    trackerId,
    shouldFetch: false,
  });
  const { activeUniqueEntries } = useConfigQuery({
    companyId,
    configName: tracker?.configNameForFilters,
  });
  const { dispatch } = useTrackerContext();
  const { tasks } = useTrackerTasksQuery({
    trackerId,
    page: 0,
    size: 1000,
    shouldFetch: false,
  });

  // Use tracker store directly
  const { trackerState, setTrackerState } = useTrackerStore();
  const selectedFilters = useMemo(
    () => trackerState?.[companyId]?.[trackerId]?.filters || [],
    [trackerState, companyId, trackerId],
  );

  const filterValues = useMemo(() => {
    const filterValues: Record<string, any> = {};
    selectedFilters.forEach((filter) => {
      filterValues[filter.filterName] = filter.filterValues;
    });
    return filterValues;
  }, [selectedFilters]);

  const filtersForm = useForm<Record<string, any>>({
    resolver: yupResolver(
      Yup.object().shape(
        getCustomFieldsValidationSchema({
          fields: activeUniqueEntries,
          doNotAddRequiredValidation: true,
        }),
      ),
    ),
    defaultValues: getCustomFieldsDefaultValues({
      data: filterValues,
      fields: activeUniqueEntries,
    }),
  });

  const {
    formState: { isSubmitting },
    handleSubmit,
    reset,
  } = filtersForm;

  useEffect(() => {
    reset(
      getCustomFieldsDefaultValues({
        data: filterValues,
        fields: activeUniqueEntries,
      }),
    );
  }, [activeUniqueEntries, filterValues]);

  const closeModal = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  // Switch-case for special fields, fallback to default
  const renderField = (filter: ConfigEntryType) => {
    switch (filter.entryCode) {
      case 'status':
        return <TaskStatusMultiSelectField field={filter} />;
      case 'applicableTo':
        return <TaskApplicableToMultiSelectField field={filter} />;
      case 'type':
        return <TaskTypeMultiSelectField field={filter} />;
      case 'impact':
        return <TaskImpactMultiSelectField field={filter} />;
      default:
        return <RenderFilterFieldByType field={filter} />;
    }
  };

  const filterTasksByFilters = (filters: FeedFilter[]) => {
    if (!filters?.length) return tasks;
    return (tasks || []).filter((task) =>
      filters.every((filter) => {
        const value = task[filter.filterName];
        if (Array.isArray(filter.filterValues)) {
          return filter.filterValues.length === 0 || filter.filterValues.includes(value);
        }
        return true;
      }),
    );
  };

  const handleApply = useCallback(
    (values: Record<string, any>) => {
      const appliedFilters: FeedFilter[] = activeUniqueEntries
        .map((filter) => ({
          filterName: filter.entryCode,
          filterValues: values[filter.entryCode] ?? [],
        }))
        .filter((f) => Array.isArray(f.filterValues) && f.filterValues.length > 0);

      setTrackerState(companyId, trackerId, { filters: appliedFilters });

      const filteredTasks = filterTasksByFilters(appliedFilters);
      dispatch({
        payload: { filteredTasks },
      });

      closeModal();
    },
    [activeUniqueEntries, setTrackerState, companyId, trackerId, tasks, dispatch, closeModal],
  );

  const resetFilters = () => {
    reset(getCustomFieldsDefaultValues({ fields: activeUniqueEntries }));
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      sx={{ '& .MuiPaper-root': { minHeight: '50%' } }}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      onClose={closeModal}
    >
      <CustomDialogTitle onClose={closeModal} id="dialog-title">
        <Title>Filters</Title>
      </CustomDialogTitle>
      <DialogContent dividers id="dialog-description" sx={{ py: 4 }}>
        <FormProvider {...filtersForm}>
          {activeUniqueEntries.map((filter) => (
            <FieldWrapper
              key={filter.id}
              id={filter.entryCode}
              label={filter.referenceObject?.label || filter.entryDescription}
              disabled={isSubmitting}
            >
              {renderField(filter)}
            </FieldWrapper>
          ))}
        </FormProvider>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-end' }}>
        <Box>
          <Button variant="text" disabled={isSubmitting} onClick={resetFilters} sx={{ mr: 0.5 }}>
            Reset
          </Button>
          <Button variant="contained" disabled={isSubmitting} onClick={handleSubmit(handleApply)}>
            Apply
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

const Title = styled(Typography, { label: 'Title' })(({ theme }) => ({
  fontSize: 20,
  fontWeight: 700,
  color: theme.palette.custom.black,
}));

export default TrackerFiltersModal;
